import _ from 'lodash';
import {
  GroupedMeasurementResponse,
  NodeResult,
  ScoreEWSAndTOBS,
  scoreEWSAndTOBSNodeResult,
} from '@/services/api.types';
import { AlarmSeverity } from '@/constants/patient';
import {
  MEASUREMENT_TYPES,
  SCORE_TYPES,
  SYSTOLIC,
} from '@/constants/measurement';

export enum MeasurementSeverity {
  Gray,
  Blue,
  Green,
  Yellow,
  Orange,
  GreenCheckmark,
  Red,
  NoAlarm,
  All,
}
export const SeverityColorMapping: { [key: number]: string } = {
  [MeasurementSeverity.Red]: 'red',
  [MeasurementSeverity.Yellow]: 'yellow',
  [MeasurementSeverity.Green]: 'green',
  [MeasurementSeverity.Blue]: 'blue',
  [MeasurementSeverity.Gray]: 'gray',
  [MeasurementSeverity.Orange]: 'orange',
  [MeasurementSeverity.NoAlarm]: 'unknown',
};

export const MeasurementSeveritySort = [
  MeasurementSeverity.Red,
  MeasurementSeverity.Yellow,
  MeasurementSeverity.Green,
  MeasurementSeverity.GreenCheckmark,
  MeasurementSeverity.Gray,
  MeasurementSeverity.NoAlarm,
];

export type Measurement = {
  type: string;
  value?: string;
  time?: number;
  measurementUnit?: string;
  severity?: MeasurementSeverity;
  ack?: {
    by: string;
    time: number;
  };
  tieredType?: TieredType;
  measurements?: (number | number[])[];
  id?: number;
};

type BroadMeasurementType = 'text' | 'measurement' | 'score';

type TieredType = {
  type: BroadMeasurementType;
  subtype: string;
};

// One column = ONE SUBMISSION (2026-08-19 user ruling; before this, a column
// was a fixed 6-hour slice of the query window and every submission inside it
// stacked into one cell). `id` is the cell key; `time` is the submission's
// instant in unix SECONDS, carried separately because the id must stay a pure
// identity — two submissions can share a minute, so time cannot be the key.
export type TableColumnDataPlot = {
  id: string;
  time: number;
};

export type TableCellData = {
  [key: string]: NodeResult[] | string | Score[];
};

export enum MeasurementRowType {
  Value,
  MultiChoice,
  Score,
  Gap,
}

type MeasurementRowConfig = {
  label: string;
  type: MeasurementRowType;
  unit: string;
};

export type EWSAndTOBSScoreItem = {
  score: number;
  factors: Array<{
    measurementType: string;
    /* usually, measurementType can be used as the label key to display i18n messages,
    but for systolic blood pressure, it's different.
     */
    labelKey?: string | undefined;
    value: string;
    score: number;
  }>;
};

export type Score = {
  id: number;
  date: number;
  value: number | string;
  tieredType: TieredType;
  severity?: AlarmSeverity | string;
  scoreFactors?: EWSAndTOBSScoreItem['factors'] | scoreEWSAndTOBSNodeResult[];
  acknowledged_by: string | undefined;
  acknowledged_date: number | undefined;
  // PD-2977 — the acknowledging tenant's KEY when the acknowledgement came from
  // the other tenant, `''` otherwise. Carried through the table view-model so
  // the column header can name the institution; resolved to copy at render
  // (utils/transfer-peer.ts), never stored as a spelling.
  acknowledged_by_tenant?: string;
  // PD-2969 — this score belongs to a submission that was imported from the
  // other tenant at acceptance. Tints the score cell, and keeps the row out of
  // the column's acknowledgement arithmetic.
  is_transfer_history: boolean;
  acknowledage_sys_logo_url?: string;
  // Batch 010 / ADR-6 — the real Calculation uuid, which is what the ack call
  // sends. `id` above is the assembler's numeric surrogate and cannot carry it.
  calc_id?: string;
};

export type ScoreTOBSAndEWS2Rule = {
  noMinEqual?: boolean;
  min?: number;
  noMaxEqual?: boolean;
  max?: number;
  equal?: number;
  result?: number;
};

export const measurementRowConfigs: MeasurementRowConfig[] = [
  {
    label: SCORE_TYPES.EWS,
    type: MeasurementRowType.Score,
    unit: '',
  },
  {
    label: SCORE_TYPES.TOBS,
    type: MeasurementRowType.Score,
    unit: '',
  },
  {
    label: '',
    type: MeasurementRowType.Gap,
    unit: '',
  },
  {
    label: MEASUREMENT_TYPES.RESPIRATORY_RATE,
    type: MeasurementRowType.Value,
    unit: 'pr.min',
  },
  {
    label: MEASUREMENT_TYPES.SATURATION,
    type: MeasurementRowType.Value,
    unit: 'SpO₂%',
  },
  {
    label: MEASUREMENT_TYPES.OXYGEN_FLOW,
    type: MeasurementRowType.Value,
    unit: 'L/min',
  },
  {
    label: MEASUREMENT_TYPES.PULSE,
    type: MeasurementRowType.Value,
    unit: 'slag/min',
  },
  {
    label: MEASUREMENT_TYPES.BLOOD_PRESSURE,
    type: MeasurementRowType.Value,
    unit: 'mmHg',
  },
  {
    label: MEASUREMENT_TYPES.LEVEL_OF_CONSCIOUSNESS,
    type: MeasurementRowType.MultiChoice,
    unit: '',
  },
  {
    label: MEASUREMENT_TYPES.TEMPERATURE,
    type: MeasurementRowType.Value,
    unit: '°C',
  },
  {
    label: MEASUREMENT_TYPES.WEIGHT,
    type: MeasurementRowType.Value,
    unit: 'kg',
  },
];

// ── per-submission columns (2026-08-19 user ruling) ─────────────────────────
//
// The query window (from/to) stays the FETCH boundary and nothing else: columns
// derive from the submissions actually delivered, never from slicing the window
// into fixed intervals. The old slicing also silently DROPPED any leaf whose
// bucket fell outside [from,to] — a backdated leaf inside an in-window
// submission just vanished; deriving columns from the data has no such edge.

// A submission column's key. `completed_questionnaire.id` is the assembler's
// numeric submission surrogate — equal across every leaf of one submission,
// unique across the response, present in BOTH data modes (self-fetch and the
// transfer-preview bundle go through the same assembler). The `sub:` prefix
// keeps it from ever colliding with an orphan-score column key.
const submissionColumnId = (m: NodeResult): string =>
  `sub:${m.completed_questionnaire?.id}`;

// The `multiple_choice_type` the assembler gives the BP site answer (LOINC
// 41904-4, `LEAF_CONFIGS` in assemble-grouped.ts).
const BLOOD_PRESSURE_SITE_CHOICE = 'BLOOD_PRESSURE_POSITION';

// An orphan-score column: a calculation whose submission is not in the
// response (the score window out-ranging the measurement window at its edges).
// The old bucketing displayed such scores, so they keep a column of their own
// rather than disappearing.
const orphanScoreColumnId = (s: ScoreEWSAndTOBS): string =>
  `calc:${s.score_time}`;

// The submission instant of a column, unix seconds. `submitted_time` is what
// the assembler stamps from Msm.submittedAt; the max leaf time is the fallback
// for a row assembled without it, so a missing stamp degrades to a sensible
// header time instead of 1970.
const submissionTime = (m: NodeResult): number =>
  m.completed_questionnaire?.submitted_time || m.completion_time || 0;

// The column a score belongs to. Scores and submissions join on the SAME key
// rule ③ already joins factors with: Calc.at == Msm.submittedAt, both carried
// into the view-model as unix seconds (score_time / submitted_time). An exact
// id linkage does not exist on the view-model — the calculation row never
// carries the submission surrogate — so this equality IS the id linkage,
// by the batch-pinned join rule rather than by timestamp coincidence.
const scoreColumnId = (
  s: ScoreEWSAndTOBS,
  submissionTimeById: Map<string, number>
): string => {
  for (const [id, time] of submissionTimeById) {
    if (time === s.score_time) {
      return id;
    }
  }
  return orphanScoreColumnId(s);
};

const submissionTimesById = (
  measurements: GroupedMeasurementResponse['measurements']
): Map<string, number> => {
  const byId = new Map<string, number>();
  Object.values(measurements ?? {}).forEach(group =>
    (group ?? []).forEach(m => {
      const id = submissionColumnId(m);
      // max over the leaves, so a submission whose stamp is absent still gets
      // its newest leaf's time.
      byId.set(id, Math.max(byId.get(id) ?? 0, submissionTime(m)));
    })
  );
  return byId;
};

// One plot per submission in the response, plus one per orphan score —
// newest leftmost (the direction the window slicing already rendered).
export const generateSubmissionPlots = (
  measurements: GroupedMeasurementResponse['measurements'],
  scores: GroupedMeasurementResponse['scores']
): TableColumnDataPlot[] => {
  const byId = submissionTimesById(measurements);

  [...(scores?.score_ews ?? []), ...(scores?.score_tobs ?? [])].forEach(s => {
    const id = scoreColumnId(s, byId);
    if (!byId.has(id)) {
      byId.set(id, s.score_time);
    }
  });

  return [...byId.entries()]
    .map(([id, time]) => ({ id, time }))
    .sort((a, b) => b.time - a.time || b.id.localeCompare(a.id));
};

export const generateCellByRowsAndPlots = (
  measurements: GroupedMeasurementResponse['measurements'],
  scores: GroupedMeasurementResponse['scores'],
  plots: TableColumnDataPlot[]
): TableCellData[] | void => {
  const availablePlotIds = plots.map(p => p.id);
  const submissionTimeById = submissionTimesById(measurements);
  const result = measurementRowConfigs.map(r => {
    const obj: TableCellData = {
      vertical_header: r.label,
    };

    const copyMeasurements = _.cloneDeep(measurements);
    const multiChoice = copyMeasurements['MULTIPLE_CHOICE'] ?? [];

    if (r.type === MeasurementRowType.MultiChoice) {
      multiChoice
        .filter(m => m.multiple_choice_type === 'LEVEL_OF_CONSCIOUSNESS')
        .forEach(m => {
          const key = submissionColumnId(m);
          if (availablePlotIds.includes(key)) {
            if (obj[key]) {
              (obj[key] as NodeResult[]).push(m);
            } else {
              obj[key] = [m];
            }
          }
        });
    } else {
      // PD-3047 — the BP value tooltip prefixes the reading with `result`, and
      // the only answer that belongs there is the BP site of the SAME
      // submission. Every other multiple-choice answer (consciousness today,
      // anything added later) stays off the row nodes: copying them all let the
      // last one in leaf order win, and the read face does not order leaves.
      // Matched by type, never by position.
      const bpSites =
        r.label === MEASUREMENT_TYPES.BLOOD_PRESSURE
          ? multiChoice.filter(
              c => c.multiple_choice_type === BLOOD_PRESSURE_SITE_CHOICE
            )
          : [];
      (copyMeasurements[r.label] ?? []).forEach(m => {
        const site = bpSites.find(
          c => c.completed_questionnaire?.id === m.completed_questionnaire?.id
        );
        if (site) {
          m.result = site.result;
        }
        const key = submissionColumnId(m);
        if (availablePlotIds.includes(key)) {
          if (obj[key]) {
            (obj[key] as NodeResult[]).push(m);
          } else {
            obj[key] = [m];
          }
        }
      });

      if (r.label === SCORE_TYPES.EWS) {
        scores?.score_ews?.forEach(score => {
          const key = scoreColumnId(score, submissionTimeById);
          if (availablePlotIds.includes(key)) {
            const ewsScore = scoreEWSAndTOBS(score, SCORE_TYPES.EWS);
            if (ewsScore) {
              if (obj[key]) {
                (obj[key] as Score[]).push(ewsScore);
              } else {
                obj[key] = [ewsScore];
              }
            }
          }
        });
      }

      if (r.label === SCORE_TYPES.TOBS) {
        scores?.score_tobs?.forEach(score => {
          const key = scoreColumnId(score, submissionTimeById);
          if (availablePlotIds.includes(key)) {
            const tobsScore = scoreEWSAndTOBS(score, SCORE_TYPES.TOBS);
            if (tobsScore) {
              if (obj[key]) {
                (obj[key] as Score[]).push(tobsScore);
              } else {
                obj[key] = [tobsScore];
              }
            }
          }
        });
      }
    }

    plots.forEach(p => {
      if (!obj[p.id]) {
        obj[p.id] = [];
      }
    });

    return obj;
  });
  return result;
};

const scoreEWSAndTOBS = (
  ewsAndTobsScore: ScoreEWSAndTOBS,
  subtype: string
): Score | null => {
  const scores: EWSAndTOBSScoreItem['factors'] = [];

  if (!ewsAndTobsScore?.score && ewsAndTobsScore?.score !== 0) {
    return null;
  }

  ewsAndTobsScore?.results?.forEach(result => {
    scores.push({
      measurementType: result.mea_type,
      labelKey:
        result.mea_type === MEASUREMENT_TYPES.BLOOD_PRESSURE
          ? SYSTOLIC
          : undefined,
      value: result.value + '',
      score: result.score || 0,
    });
  });

  return {
    id: ewsAndTobsScore?.id,
    value: ewsAndTobsScore?.score,
    date: ewsAndTobsScore?.score_time,
    is_transfer_history: ewsAndTobsScore?.is_transfer_history,
    severity: ewsAndTobsScore?.severity?.toLowerCase(),
    scoreFactors: scores,
    tieredType: { type: 'score', subtype },
    acknowledged_by: ewsAndTobsScore?.acknowledged_by,
    acknowledged_date: ewsAndTobsScore?.acknowledged_date,
    acknowledged_by_tenant: ewsAndTobsScore?.acknowledged_by_tenant,
    acknowledage_sys_logo_url: ewsAndTobsScore?.acknowledage_sys_logo_url,
    calc_id: ewsAndTobsScore?.calc_id,
  };
};

export function uniqWithLast<T>(
  array: T[],
  comparator: (a: T, b: T) => boolean
): T[] {
  return _.uniqWith(_.reverse(_.clone(array)), comparator);
}

export enum NamedTimeIndex {
  NIGHT,
  MORNING,
  AFTERNOON,
  EVENING,
}

export const timeIndexKey: { [key: number]: string } = {
  [NamedTimeIndex.NIGHT]: 'cdb_questionnaire_result_table_night',
  [NamedTimeIndex.MORNING]: 'cdb_questionnaire_result_table_morning',
  [NamedTimeIndex.AFTERNOON]: 'cdb_questionnaire_result_table_afternoon',
  [NamedTimeIndex.EVENING]: 'cdb_questionnaire_result_table_evening',
};
